import { z } from 'zod'
import {
  createListQuerySchema,
  createFilterableListQuerySchema,
  baseFilterBySchema,
  isoDateTimeSchema,
  ORDER_DIRECTIONS,
  paginationSchema,
} from '@customTypes/cloudmix/api'
import {
  workflowSchema,
  workflowSessionIdSchema,
} from '@customTypes/cloudmix/workflow'
import { mediaFormatSchema } from '@customTypes/cloudmix/common/mediaFormat'
import { sessionStatusSchema } from '@customTypes/cloudmix/sessions/status'
import { orgIdSchema } from '@features/organisations/types'
import { projectIdSchema } from '../projects'

export const DEFAULT_LIST_ORDER = [
  { field: 'status', direction: ORDER_DIRECTIONS.ascending },
  { field: 'startedAt', direction: ORDER_DIRECTIONS.descending },
]

export const SESSION_ORDER_BY_FIELDS = [
  'name',
  'status',
  'startedAt',
  'stoppedAt',
] as const

export const STAFF_SESSION_ORDER_BY_FIELDS = [
  'startedAt',
  'name',
  'status',
] as const

const projectParentResource = z.object({
  type: z.literal('project'),
  projectId: projectIdSchema,
})

const parentResourceSchema = z.discriminatedUnion('type', [
  projectParentResource,
])
export type ParentResource = z.infer<typeof parentResourceSchema>
export type ProjectParentResource = z.infer<typeof projectParentResource>

export function isProjectResource(
  resource: ParentResource | undefined
): resource is ProjectParentResource {
  return resource?.type === 'project'
}

const sessionsDetailsSchema = z.object({
  workflowSessionId: workflowSessionIdSchema,
  name: z.string(),
  workflow: workflowSchema,
  mediaFormat: mediaFormatSchema,
  status: sessionStatusSchema,
  startedAt: isoDateTimeSchema.nullable(),
  stoppedAt: isoDateTimeSchema.nullable(),
  engineId: z.string(),
  parentResource: parentResourceSchema,
})
export type SessionsDetails = z.infer<typeof sessionsDetailsSchema>

const staffSessionDetailsSchema = sessionsDetailsSchema.extend({
  orgId: orgIdSchema,
})
export type StaffSessionsDetails = z.infer<typeof staffSessionDetailsSchema>

const listSessionsResponseSchema = z
  .object({
    items: z.array(sessionsDetailsSchema),
  })
  .and(paginationSchema)

export type ListSessionsResponse = z.infer<typeof listSessionsResponseSchema>

export const listSessionRequestPaginationSchema = createListQuerySchema(
  [...SESSION_ORDER_BY_FIELDS],
  {
    orderBy: DEFAULT_LIST_ORDER,
  }
)
export const listSessionsRequestSchema = listSessionRequestPaginationSchema.and(
  z.object({
    parentResourceId: z.string().optional(),
    status: sessionStatusSchema.optional(),
  })
)

export type ListSessionsRequest = z.infer<typeof listSessionsRequestSchema>

export const staffSessionsResponseSchema = z
  .object({
    items: z.array(staffSessionDetailsSchema),
  })
  .and(paginationSchema)
export type StaffSessionsResponse = z.infer<typeof staffSessionsResponseSchema>

export const staffSessionRequestSchema = createListQuerySchema(
  [...STAFF_SESSION_ORDER_BY_FIELDS],
  {
    orderBy: DEFAULT_LIST_ORDER,
  }
)
export type StaffSessionRequest = z.infer<typeof staffSessionRequestSchema>

export const listOrgSessionRequestSchema = createFilterableListQuerySchema(
  [...SESSION_ORDER_BY_FIELDS],
  baseFilterBySchema,
  {
    orderBy: DEFAULT_LIST_ORDER,
  }
).extend({
  status: sessionStatusSchema.optional(),
})
export type ListOrgSessionRequest = z.infer<typeof listOrgSessionRequestSchema>

export const updateSessionSchema = z.object({
  name: z.string(),
})
export type UpdateSession = z.infer<typeof updateSessionSchema>
